import React from 'react';
import { Box, Notification, TextLink, Typography } from '@drone-js/core';
import PopoverCalendar from 'component/page/detail/PopoverCalendar/PopoverCalendar';
import BookingOptionDateChipContainer from 'component/page/detail/PackageOptions/ExpandedOffer/OfferCardDateChip/OfferCardDateChip';
import { mapStateToProps, Props } from 'component/page/detail/PackageOptions/ExpandedOffer/OfferCardDateSelection/OfferCardDateSelection.selectors';
import { connect, useSelector } from 'react-redux';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesCloseDatePickerTextlinkClickEventActivitiesManifestV1,
    ActivitiesCloseDatePickerTextlinkSeenEventActivitiesManifestV1,
    ActivitiesPleaseSelectDateNotificationSeenEventActivitiesManifestV1,
    ActivitiesSeeOtherDatesTextlinkClickEventActivitiesManifestV1,
    ActivitiesSeeOtherDatesTextlinkSeenEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import {
    ActivitiesCloseDatePickerTextlinkClickEventActivitiesFieldsV1,
    ActivitiesCloseDatePickerTextlinkSeenEventActivitiesFieldsV1,
    ActivitiesPleaseSelectDateNotificationSeenEventActivitiesFieldsV1,
    ActivitiesSeeOtherDatesTextlinkClickEventActivitiesFieldsV1,
    ActivitiesSeeOtherDatesTextlinkSeenEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import { selectScreenSize } from 'store/slice/screen';
import { killEvent } from 'core/utils/domUtils';

const OfferCardDateSelection: React.FC<Props> = (props) => {
    const screenSize = useSelector(selectScreenSize);
    const [isShowCalendar, setIsShowCalendar] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (props.isShowNotification) {
            trackElementEvent<ActivitiesPleaseSelectDateNotificationSeenEventActivitiesFieldsV1>(
                ActivitiesPleaseSelectDateNotificationSeenEventActivitiesManifestV1.ids,
                {},
                { offer_id: props.selectedOfferId, activity_id: props.activityId },
            );
        }
    }, [props.isShowNotification, props.selectedOfferId, props.activityId]);

    React.useEffect(() => {
        if (!screenSize.tabletOrDesktop) {
            if (isShowCalendar) {
                trackElementEvent<ActivitiesCloseDatePickerTextlinkSeenEventActivitiesFieldsV1>(
                    ActivitiesCloseDatePickerTextlinkSeenEventActivitiesManifestV1.ids,
                    {},
                    { offer_id: props.selectedOfferId, activity_id: props.activityId },
                );
            } else {
                trackElementEvent<ActivitiesSeeOtherDatesTextlinkSeenEventActivitiesFieldsV1>(
                    ActivitiesSeeOtherDatesTextlinkSeenEventActivitiesManifestV1.ids,
                    {},
                    { offer_id: props.selectedOfferId, activity_id: props.activityId },
                );
            }
        }
    }, [screenSize.tabletOrDesktop, isShowCalendar, props.selectedOfferId, props.activityId]);

    const onSeeOtherDatesClick = (e: React.MouseEvent) => {
        killEvent(e);
        trackElementEvent<ActivitiesSeeOtherDatesTextlinkClickEventActivitiesFieldsV1>(
            ActivitiesSeeOtherDatesTextlinkClickEventActivitiesManifestV1.ids,
            {},
            { offer_id: props.selectedOfferId, activity_id: props.activityId },
        );
        setIsShowCalendar(true);
    };

    const onCloseDatePickerClick = (e: React.MouseEvent) => {
        killEvent(e);
        trackElementEvent<ActivitiesCloseDatePickerTextlinkClickEventActivitiesFieldsV1>(
            ActivitiesCloseDatePickerTextlinkClickEventActivitiesManifestV1.ids,
            {},
            { offer_id: props.selectedOfferId, action_element_value: 'offer_level', activity_id: props.activityId },
        );
        setIsShowCalendar(false);
    };

    return (
        <Box display="flex" flexDirection="column" me={screenSize.tabletOrDesktop ? '24' : undefined} maxWidth={screenSize.tabletOrDesktop ? '320px' : undefined}>
            <Box display="flex" justifyContent="space-between" width="100%" mb="16" flexDirection="row">
                <Typography variant="body-default-strong" textColor="base.default" tag="h4">
                    {props.cms[154600]}
                </Typography>
                {!screenSize.tabletOrDesktop &&
                    (isShowCalendar ? (
                        <TextLink
                            label={props.cms[290834]}
                            data-element-name="activities-close-date-picker-textlink"
                            data-testid="activities-close-date-picker-textlink"
                            textVariant="body-s"
                            onClick={onCloseDatePickerClick}
                        />
                    ) : (
                        <TextLink
                            label={props.cms[305272]}
                            data-element-name="activities-see-other-dates-textlink"
                            data-testid="activities-see-other-dates-textlink"
                            textVariant="body-s"
                            onClick={onSeeOtherDatesClick}
                        />
                    ))}
            </Box>
            {props.isShowNotification && (
                <Box mb="16">
                    <Notification
                        data-element-name="activities-please-select-date-notification"
                        data-testid="activities-please-select-date-notification"
                        intent="warning"
                        title={props.cms[304946]}
                        asset={{ id: 'fill.symbol.circle-info' }}
                        radius="m"
                    />
                </Box>
            )}
            {screenSize.tabletOrDesktop ? (
                <Box width="100%" me="24">
                    <PopoverCalendar isOfferLevel={true} />
                </Box>
            ) : (
                <BookingOptionDateChipContainer isShowCalendar={isShowCalendar} onCalendarClick={() => setIsShowCalendar(false)} />
            )}
        </Box>
    );
};

OfferCardDateSelection.displayName = 'OfferCardDateSelection';
export default connect(mapStateToProps)(OfferCardDateSelection);
